/**
 * PO composer · right-rail live document preview.
 *
 * Faithful Tailwind translation of the prototype's `.composer-preview` →
 * `.preview-card` → `.preview-doc` block:
 *
 *   - `.preview-head`      → raised strip, "Order summary" + pulsing live-dot.
 *   - `.preview-doc-head`  → `.preview-from` (our company name / address /
 *                            GSTIN) on the left; `.preview-doc-title`
 *                            ("Purchase order" + mono number + Draft pill) right.
 *   - `.preview-meta-grid` → 2-col grid: Vendor · Order/Expected dates ·
 *                            Payment terms · Ship to.
 *   - `.preview-items`     → item table (Item w/ HSN+GST meta-line · Qty ·
 *                            Rate · Amount), last row's border dropped.
 *   - `.preview-totals`    → right-aligned Subtotal · GST · `.grand` Total.
 *   - `.preview-summary`   → "what happens on save" cells + "Operator only"
 *                            match-pill.
 *
 * Every figure is driven by live form state + the deterministic paise math —
 * nothing is hardcoded demo copy. The company block reads `identity.me`.
 *
 * @see prototype/purchases/purchase-order-new.html §"Right column · Summary rail"
 */
import { Check, FileText } from "lucide-react";
import { paiseToInrExact } from "../../../lib/format.js";
import { computeLineTaxablePaise, type PoTotals } from "../lib/po-math.js";
import type { PoLineItem } from "../lib/form-schema.js";
import type { PoVendorOption } from "./po-vendor-section.js";

interface PoPreviewProps {
  poNumber: string;
  status: string;
  vendor: PoVendorOption | null;
  orderDate: string;
  expectedDate: string;
  lineItems: PoLineItem[];
  totals: PoTotals;
  /** Signed-in company — drives the printable "from" block. */
  company: { legalName: string; tradeName: string | null; state: string | null } | null;
}

export function PoPreview({
  poNumber,
  status,
  vendor,
  orderDate,
  expectedDate,
  lineItems,
  totals,
  company,
}: PoPreviewProps) {
  const companyName = company?.tradeName || company?.legalName || "Your company";
  const isDraft = status === "draft";
  const renderedLines = lineItems.filter((l) => l.description.trim() !== "");

  return (
    <aside className="sticky top-20 self-start">
      <div className="overflow-hidden rounded-md border border-line bg-paper shadow-card">
        {/* .preview-head */}
        <header className="flex items-center justify-between gap-3 border-b border-line bg-raised px-[18px] py-3.5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-emerald-500 shadow-[0_0_0_3px] shadow-emerald-50" />
            Order summary
          </h3>
        </header>

        {/* .preview-doc */}
        <div className="px-[22px] pb-6 pt-[22px] text-xs leading-[1.55] text-ink-2">
          {/* .preview-doc-head */}
          <div className="mb-3.5 flex items-start justify-between gap-[18px] border-b border-line pb-3.5">
            <div className="min-w-0">
              <span className="mb-0.5 block text-sm font-bold tracking-tight text-ink">
                {companyName}
              </span>
              {company?.state ? (
                <span className="block text-xs leading-normal text-muted">{company.state}</span>
              ) : null}
            </div>
            <div className="shrink-0 text-right text-xs font-semibold uppercase tracking-[0.08em] text-muted">
              Purchase order
              <span className="mt-1 block text-sm font-bold normal-case tracking-tight text-ink">
                {poNumber}
              </span>
              {isDraft ? (
                <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-line bg-raised px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  <FileText className="h-2.5 w-2.5" />
                  Draft
                </span>
              ) : (
                <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-sky-100 bg-sky-50 px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.06em] text-sky-700">
                  {status.replace(/_/g, " ")}
                </span>
              )}
            </div>
          </div>

          {/* .preview-meta-grid */}
          <div className="mb-3.5 grid grid-cols-2 gap-x-4 gap-y-2.5 border-b border-line pb-3.5">
            <MetaCell label="Vendor">
              <span className="block text-xs text-ink">
                <strong className="font-semibold">{vendor?.name ?? "No vendor selected"}</strong>
              </span>
              {vendor?.city ? (
                <span className="block text-xs text-muted">{vendor.city}</span>
              ) : null}
            </MetaCell>
            <MetaCell label="Order date" mono>
              <strong className="font-semibold">{formatDocDate(orderDate)}</strong>
              <span className="mt-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                Expected delivery
              </span>
              <strong className="font-semibold">{formatDocDate(expectedDate)}</strong>
            </MetaCell>
            <MetaCell label="Currency" mono>
              INR
            </MetaCell>
            <MetaCell label="Ship to">
              {companyName}
              {company?.state ? `, ${company.state}` : ""}
            </MetaCell>
          </div>

          {/* .preview-items */}
          <table className="mb-3 w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="border-b border-line py-1.5 text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Item
                </th>
                <th className="border-b border-line py-1.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Qty
                </th>
                <th className="border-b border-line py-1.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Rate
                </th>
                <th className="border-b border-line py-1.5 text-right text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {renderedLines.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-3 text-center text-xs text-muted">
                    Add a line item to see it here.
                  </td>
                </tr>
              ) : (
                renderedLines.map((row, i) => (
                  <tr key={row.id} className={i < renderedLines.length - 1 ? "border-b border-line" : ""}>
                    <td className="py-1.5 align-top text-ink">
                      {row.description}
                      {row.hsn || row.taxRate ? (
                        <span className="block text-xs text-muted">
                          {row.hsn ? `HSN ${row.hsn}` : ""}
                          {row.hsn && row.taxRate ? " · " : ""}
                          {row.taxRate ? `${row.taxRate}% GST` : ""}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-1.5 text-right align-top font-mono tabular-nums">
                      {row.qty || "—"}
                    </td>
                    <td className="py-1.5 text-right align-top font-mono tabular-nums">
                      {row.rate || "—"}
                    </td>
                    <td className="py-1.5 text-right align-top font-mono tabular-nums">
                      {paiseToInrExact(computeLineTaxablePaise(row)).replace("₹", "")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* .preview-totals */}
          <div className="ml-auto mt-1 max-w-[240px] border-t border-line pt-3">
            <TotalRow label="Subtotal" value={paiseToInrExact(totals.subtotalPaise).replace("₹", "")} />
            <TotalRow label="GST" value={paiseToInrExact(totals.taxPaise).replace("₹", "")} />
            <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-line pb-1 pt-2 text-sm font-bold text-ink">
              <span>Total</span>
              <span className="font-mono text-sm font-bold tracking-tight tabular-nums">
                {paiseToInrExact(totals.totalPaise)}
              </span>
            </div>
          </div>
        </div>

        {/* .preview-summary */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-line px-[18px] py-3.5">
          <SummaryCell
            label="What happens on save"
            value={
              vendor ? `PO sent to ${vendor.name}` : "PO is numbered and sent to the vendor on send"
            }
          />
          <SummaryCell label="Next step" value="Log goods receipt when goods arrive" />
          <SummaryCell label="Then" value="Draft bill is created and matched to this PO" />
          <div className="min-w-0">
            <span className="mb-0.5 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">
              Approval needed
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-2 py-px text-xs font-semibold text-emerald-700">
              <Check className="h-[11px] w-[11px]" strokeWidth={2.4} />
              Operator only
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}

function MetaCell({
  label,
  mono = false,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <span className="mb-0.5 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">
        {label}
      </span>
      <span className={`block text-xs leading-normal text-ink ${mono ? "font-mono tabular-nums" : ""}`}>
        {children}
      </span>
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 text-xs text-muted">
      <span>{label}</span>
      <span className="font-mono font-medium tabular-nums text-ink">{value}</span>
    </div>
  );
}

function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <span className="mb-0.5 block text-xs font-semibold uppercase tracking-[0.08em] text-muted">
        {label}
      </span>
      <span className="block text-xs leading-relaxed text-muted">{value}</span>
    </div>
  );
}

/** ISO date → "06 May 2026" for the printable preview. "—" when absent. */
function formatDocDate(iso: string): string {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return iso;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthLabel = months[Number(month) - 1] ?? month;
  return `${day} ${monthLabel} ${year}`;
}
